import React, { useState, useRef, useEffect } from "react";
import { BarChart3, LineChart, Hash, Table2, Search, Info, X, GripVertical } from "lucide-react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import MetricBlock from "./MetricBlock";
import FunnelQueryConfig from "./FunnelQueryConfig";
import FlowQueryConfig from "./FlowQueryConfig";
import RetentionQueryConfig from "./RetentionQueryConfig";
import EventPropertyFilterModule from "../../../conversion-events/components/EventPropertyFilterModule";
import { EventPropertyFilterChip } from "../../../conversion-events/components/EventsFilterModule";
import TransactionPropertyFilterModule from "../../../conversion-events/components/TransactionPropertyFilterModule";
import TransactionPropertyFilterChip from "../../../conversion-events/components/TransactionPropertyFilterChip";
import GlobalPropertyFilterModule from "../../../conversion-events/components/GlobalPropertyFilterModule";

// Theme palettes (name + array of hex colors for strip)
const CHART_THEMES = [
  { id: "mixpanel-default", label: "Cryptique Default", colors: ["#a78bfa", "#f97316", "#14b8a6", "#eab308", "#ec4899", "#6366f1"] },
  { id: "purple", label: "Purple", colors: ["#4B57AA", "#7c3aed", "#a78bfa", "#c4b5fd", "#ddd6fe"] },
  { id: "lava", label: "Lava", colors: ["#dc2626", "#ea580c", "#f97316", "#fbbf24", "#78716c"] },
  { id: "mint", label: "Mint", colors: ["#14b8a6", "#2dd4bf", "#5eead4", "#99f6e4", "#ccfbf1"] },
  { id: "deuteranopia", label: "Deuteranopia", colors: ["#3b82f6", "#22c55e", "#eab308", "#f97316"] },
  { id: "protanopia", label: "Protanopia", colors: ["#eab308", "#a16207", "#dc2626", "#a78bfa"] },
  { id: "tritanopia", label: "Tritanopia", colors: ["#3b82f6", "#22c55e", "#ec4899", "#f59e0b"] },
  { id: "focus", label: "Focus", colors: ["#f97316", "#64748b", "#475569", "#334155", "#1e293b"] },
  { id: "test", label: "test", colors: ["#ec4899", "#14b8a6", "#eab308", "#a78bfa", "#f97316"] },
];

const TABS = [
  { id: "query", label: "Query" },
  { id: "chart", label: "Chart" },
  { id: "annotations", label: "Annotations" },
];

const CHART_TYPES = [
  { id: "line", label: "Line", icon: LineChart },
  { id: "bar", label: "Bar", icon: BarChart3 },
  { id: "metric", label: "Metric", icon: Hash },
  { id: "table", label: "Table", icon: Table2 },
];

export default function QueryBuilderPanel({
  activeTab = "query",
  onTabChange,
  reportType = "insight", // 'insight' | 'funnel' | 'flow' | 'retention' - Query tab shows appropriate config
  // Funnel-specific (when reportType === 'funnel')
  funnelSteps = [],
  onFunnelStepsChange = null,
  // Flow-specific (when reportType === 'flow')
  flowAnchorEvents = [],
  onFlowAnchorEventsChange = null,
  flowConversionWindow = { value: 7, unit: 'days' },
  onFlowConversionWindowChange = null,
  flowConversionCriteria = 'distinct_id',
  onFlowConversionCriteriaChange = null,
  // Retention-specific (when reportType === 'retention')
  retentionStartEvent = "",
  onRetentionStartEventChange = null,
  retentionReturnEvent = "",
  onRetentionReturnEventChange = null,
  retentionInterval = "day",
  onRetentionIntervalChange = null,
  retentionRule = "on_or_after",
  onRetentionRuleChange = null,
  retentionMetric = "retention_rate",
  onRetentionMetricChange = null,
  /** Column labels from last retention query (Chart tab / line view interval picker) */
  retentionIntervals = [],
  // Legacy props (for backward compatibility)
  selectedEvent,
  onEventSelect,
  selectedAggregation = "count",
  onAggregationSelect,
  // New props for multi-event metrics
  metrics = null,
  onMetricsChange = null,
  availableEvents = [],
  chartType = "line",
  onChartTypeChange,
  teamId = null,
  breakdown = null, // Legacy: single breakdown (for backward compatibility)
  onBreakdownChange = null, // Legacy
  breakdowns = [], // New: array of breakdowns
  onBreakdownsChange = null, // New: handler for breakdowns array
  visualizationConfig = null,
  onVisualizationConfigChange = null,
  // Legacy filter props (deprecated, filters are now per-event)
  metricFilters = [],
  onMetricFiltersChange,
  // Global filters props
  globalFilters = [],
  onGlobalFiltersChange = null,
  // Funnel measuring props
  funnelMeasuring = "conversion_rate",
  onFunnelMeasuringChange = null,
  funnelMeasuringProperty = null,
  onFunnelMeasuringPropertyChange = null,
  funnelMeasuringAggregation = "average",
  onFunnelMeasuringAggregationChange = null,
  funnelMeasuringPercentileValue = 50,
  onFunnelMeasuringPercentileValueChange = null,
  funnelConversionWindow = { value: 7, unit: 'days' },
  onFunnelConversionWindowChange = null,
}) {
  // Local InlineDropdown (mirrors the one used in RetentionQueryConfig)
  function InlineDropdown({
    value,
    options = [],
    onChange,
    disabled = false,
    widthClass = "min-w-[120px]",
  }) {
    const [open, setOpen] = React.useState(false);
    const menuRef = React.useRef(null);
    const buttonRef = React.useRef(null);
    const selected = options.find((o) => String(o.value) === String(value)) || options[0];

    React.useEffect(() => {
      const handleClickOutside = (e) => {
        if (!open) return;
        if (menuRef.current && menuRef.current.contains(e.target)) return;
        if (buttonRef.current && buttonRef.current.contains(e.target)) return;
        setOpen(false);
      };
      const handleEsc = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEsc);
      };
    }, [open]);

    return (
      <div className={`relative ${widthClass}`}>
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
          className="w-full h-8 px-2 text-[14px] border rounded-md flex items-center justify-between gap-2"
          style={{
            backgroundColor: "#ffffff",
            borderColor: theme.border,
            color: theme.textPrimary,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <span className="truncate text-left">{selected?.label ?? ""}</span>
          <span style={{ color: theme.textMuted }}>▾</span>
        </button>
        {open && !disabled && (
          <div
            ref={menuRef}
            className="absolute left-0 right-0 mt-1 z-[120] border rounded-lg overflow-hidden"
            style={{
              backgroundColor: theme.bgRaised,
              borderColor: theme.border,
              boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {options.map((opt) => {
              const active = String(opt.value) === String(value);
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => {
                    onChange?.(opt.value);
                    setOpen(false);
                  }}
                  className="w-full px-2 py-1.5 text-[13px] text-left transition-colors"
                  style={{
                    backgroundColor: active ? (theme.bgOverlay || "#f1f5f9") : "transparent",
                    color: active ? (theme.textPrimary || "#0f172a") : (theme.textSecondary || "#475569"),
                    borderLeft: active ? `2px solid ${theme.primary}` : "2px solid transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.backgroundColor = theme.bgOverlay || "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  const isFunnel = reportType === "funnel";
  const isFlow = reportType === "flow";
  const isRetention = reportType === "retention";

  const defaultLeftY = { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" };
  const defaultRightY = { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" };
  const defaultXAxis = { visible: true, label: "" };

  // Use new metrics array if provided, otherwise create from legacy props
  const [internalMetrics, setInternalMetrics] = useState(() => {
    if (metrics && Array.isArray(metrics)) {
      return metrics;
    }
    // Legacy: convert single event to metric format
    if (selectedEvent) {
      return [{
        id: 'm1',
        aggregation: selectedAggregation,
        axis: 'left',
        events: [{
          name: selectedEvent,
          filters: metricFilters || []
        }]
      }];
    }
    return [];
  });

  const currentMetrics = metrics || internalMetrics;
  const handleMetricsChange = onMetricsChange || setInternalMetrics;
  // Axis state (Left Y, Right Y, X) - initialize from visualizationConfig when provided
  const [leftY, setLeftY] = useState(() => {
    const axis = visualizationConfig?.axis;
    return axis?.leftY ? { ...defaultLeftY, ...axis.leftY } : defaultLeftY;
  });
  const [rightY, setRightY] = useState(() => {
    const axis = visualizationConfig?.axis;
    return axis?.rightY ? { ...defaultRightY, ...axis.rightY } : defaultRightY;
  });
  const [xAxis, setXAxis] = useState(() => {
    const axis = visualizationConfig?.axis;
    return axis?.xAxis ? { ...defaultXAxis, ...axis.xAxis } : defaultXAxis;
  });
  const [selectedThemeId, setSelectedThemeId] = useState(() => visualizationConfig?.themeId || "mixpanel-default");
  const [themeSearch, setThemeSearch] = useState("");
  const [funnelAxis, setFunnelAxis] = useState(() => visualizationConfig?.funnelAxis ?? "left");
  const [retentionLineIntervalIndex, setRetentionLineIntervalIndex] = useState(() =>
    typeof visualizationConfig?.retentionLineIntervalIndex === "number" &&
    visualizationConfig.retentionLineIntervalIndex >= 0
      ? visualizationConfig.retentionLineIntervalIndex
      : 0
  );

  // Sync external visualizationConfig into internal state when loading a saved card
  const lastAppliedConfigRef = useRef(null);
  const skipNextSyncToParentRef = useRef(false);
  useEffect(() => {
    if (!visualizationConfig || typeof visualizationConfig !== "object") return;
    // When config changes (e.g. saved config arrives after defaults), re-apply it
    const configChanged = lastAppliedConfigRef.current !== visualizationConfig;
    if (configChanged) lastAppliedConfigRef.current = visualizationConfig;
    if (!configChanged) return;
    const axis = visualizationConfig.axis;
    const hasAxis = axis && typeof axis === "object";
    skipNextSyncToParentRef.current = true; // Don't overwrite parent with our state during apply
    if (hasAxis) {
      if (axis.leftY) setLeftY((prev) => ({ ...defaultLeftY, ...prev, ...axis.leftY }));
      if (axis.rightY) setRightY((prev) => ({ ...defaultRightY, ...prev, ...axis.rightY }));
      if (axis.xAxis) setXAxis((prev) => ({ ...defaultXAxis, ...prev, ...axis.xAxis }));
    }
    if (visualizationConfig.themeId) setSelectedThemeId(visualizationConfig.themeId);
    if (visualizationConfig.funnelAxis) setFunnelAxis(visualizationConfig.funnelAxis);
    if (
      isRetention &&
      typeof visualizationConfig.retentionLineIntervalIndex === "number" &&
      visualizationConfig.retentionLineIntervalIndex >= 0
    ) {
      setRetentionLineIntervalIndex(visualizationConfig.retentionLineIntervalIndex);
    }
  }, [visualizationConfig, isRetention]);

  // Sync axis/theme to parent for persistence when they change (include theme colors for board render)
  useEffect(() => {
    if (typeof onVisualizationConfigChange !== "function") return;
    if (skipNextSyncToParentRef.current) {
      skipNextSyncToParentRef.current = false;
      return;
    }
    const theme = CHART_THEMES.find((t) => t.id === selectedThemeId);
    onVisualizationConfigChange({
      axis: { leftY, rightY, xAxis },
      themeId: selectedThemeId,
      colors: theme?.colors || undefined,
      ...(isFunnel ? { funnelAxis } : {}),
      ...(isRetention ? { retentionLineIntervalIndex } : {}),
    });
  }, [leftY, rightY, xAxis, selectedThemeId, funnelAxis, isFunnel, isRetention, retentionLineIntervalIndex, onVisualizationConfigChange]);

  // Metric management functions
  const handleAddMetric = () => {
    // Generate unique ID based on existing metric IDs to avoid conflicts
    const existingIds = currentMetrics.map(m => m.id).filter(Boolean);
    let newId = `m${currentMetrics.length + 1}`;
    let counter = currentMetrics.length + 1;
    // Ensure ID is unique
    while (existingIds.includes(newId)) {
      counter++;
      newId = `m${counter}`;
    }
    
    const newMetric = {
      id: newId,
      aggregation: 'count',
      axis: 'left',
      events: []
    };
    handleMetricsChange([...currentMetrics, newMetric]);
  };

  const handleUpdateMetric = (metricIndex, updates) => {
    const updated = [...currentMetrics];
    updated[metricIndex] = { ...updated[metricIndex], ...updates };
    handleMetricsChange(updated);
  };

  const handleRemoveMetric = (metricIndex) => {
    const updated = currentMetrics.filter((_, i) => i !== metricIndex);
    // Note: We keep existing IDs even after removal to maintain consistency
    // This is fine as long as new metrics get unique IDs
    handleMetricsChange(updated);
  };

  const handleAddEvent = (metricIndex, eventName, source = 'event') => {
    const updated = [...currentMetrics];
    const metric = updated[metricIndex];
    const key = (e) => `${e.name}\0${e.source || 'event'}`;
    const eventKey = `${eventName}\0${source}`;
    if (!metric.events.some(e => key(e) === eventKey)) {
      metric.events.push({
        name: eventName,
        source: source || 'event',
        filters: []
      });
      handleMetricsChange(updated);
    }
  };

  const handleRemoveEvent = (metricIndex, eventIndex) => {
    const updated = [...currentMetrics];
    updated[metricIndex].events = updated[metricIndex].events.filter((_, i) => i !== eventIndex);
    handleMetricsChange(updated);
  };

  const handleUpdateEventFilters = (metricIndex, eventIndex, filters) => {
    const updated = [...currentMetrics];
    updated[metricIndex].events[eventIndex].filters = filters;
    handleMetricsChange(updated);
  };

  // Separate global filters into event, transaction, and cohort filters (for chips & legacy logic)
  const globalCohortFilters = globalFilters.filter(f =>
    f.category === 'cohort' || f.propertySource === 'cohort'
  );
  const globalEventFilters = globalFilters.filter(f =>
    f.category !== 'transaction_properties' &&
    f.propertySource !== 'transaction' &&
    f.category !== 'first_time' &&
    f.category !== 'cohort' &&
    f.propertySource !== 'cohort'
  );
  const globalTransactionFilters = globalFilters.filter(f =>
    f.category === 'transaction_properties' ||
    f.propertySource === 'transaction'
  );
  const globalFirstTimeFilter = globalFilters.find(f => f.category === 'first_time');

  // Global filter state and handlers
  const [isGlobalFilterOpen, setIsGlobalFilterOpen] = useState(false);
  const [selectedGlobalFilterForEdit, setSelectedGlobalFilterForEdit] = useState(null);
  const globalFilterButtonRef = useRef(null);
  const globalFilterChipRefs = useRef({});

  // Breakdown state and handlers
  const [breakdownPickerOpen, setBreakdownPickerOpen] = useState(false);
  const [breakdownPickerIndex, setBreakdownPickerIndex] = useState(null);
  const breakdownPickerRef = useRef(null);
  
  // Use breakdowns array if provided, otherwise convert single breakdown to array for backward compatibility
  const currentBreakdowns = breakdowns && Array.isArray(breakdowns) 
    ? breakdowns 
    : (breakdown ? [breakdown] : []);
  
  const handleBreakdownsChange = (newBreakdowns) => {
    if (onBreakdownsChange) {
      onBreakdownsChange(newBreakdowns);
    } else if (onBreakdownChange) {
      // Legacy: convert array back to single breakdown
      onBreakdownChange(newBreakdowns.length > 0 ? newBreakdowns[0] : null);
    }
  };

  const handleGlobalFilterClick = () => {
    setSelectedGlobalFilterForEdit(null);
    setIsGlobalFilterOpen(true);
  };

  const handleGlobalFiltersChange = (newFilter) => {
    if (selectedGlobalFilterForEdit) {
      // Update existing filter — preserve category/propertySource so transaction filters stay connected
      const existing = globalFilters.find(f => f.id === selectedGlobalFilterForEdit.id);
      const merged = existing ? {
        ...existing,
        ...newFilter,
        id: existing.id,
        category: newFilter.category ?? existing.category,
        propertySource: newFilter.propertySource ?? existing.propertySource,
        field: newFilter.field ?? newFilter.property ?? existing.field ?? existing.property,
        property: newFilter.property ?? newFilter.field ?? existing.property ?? existing.field,
      } : newFilter;
      const updated = globalFilters.map(f =>
        f.id === selectedGlobalFilterForEdit.id ? merged : f
      );
      onGlobalFiltersChange && onGlobalFiltersChange(updated);
      setSelectedGlobalFilterForEdit(null);
    } else {
      // Add new filter coming from global popup
      let filterWithId;

      // Special handling for first_time filter - ensure it has the correct structure
      if (newFilter.category === 'first_time') {
        filterWithId = {
          id: newFilter.id || `global-first-time-${Date.now()}`,
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals', // Required for query compiler
          value: true,
          category: 'first_time',
          connector: globalFilters.length === 0 ? 'WHERE' : 'AND'
        };
      } else {
        const propName = (newFilter.property || newFilter.field || '').toString();
        const isCohortFilter = newFilter.propertySource === 'cohort' || newFilter.category === 'cohort';
        const isTransactionProp = newFilter.propertySource === 'transaction';
        const isUserProp =
          newFilter.propertySource === 'user' ||
          newFilter.category === 'user_properties' ||
          (propName && propName.startsWith('user.'));

        if (isCohortFilter) {
          filterWithId = {
            ...newFilter,
            id: newFilter.id || `global-cohort-filter-${Date.now()}-${Math.random()}`,
            category: 'cohort',
            propertySource: 'cohort',
            field: 'cohort',
            property: 'cohort',
            connector: (globalEventFilters.length === 0 && globalCohortFilters.length === 0 && globalTransactionFilters.length === 0) ? 'WHERE' : 'AND'
          };
        } else if (isTransactionProp) {
          // Transaction property (global for all transaction-based metrics)
          filterWithId = {
            ...newFilter,
            id: newFilter.id || `global-transaction-filter-${Date.now()}-${Math.random()}`,
            category: 'transaction_properties',
            propertySource: 'transaction',
            connector: globalTransactionFilters.length === 0 ? 'WHERE' : 'AND'
          };
        } else if (isUserProp) {
          // Global user property: applies to both events and transactions;
          // keep category 'global' so it's treated like other event filters in chips,
          // but propertySource stays 'user' for the query compiler.
          filterWithId = {
            ...newFilter,
            id: newFilter.id || `global-user-filter-${Date.now()}-${Math.random()}`,
            category: newFilter.category || 'global',
            propertySource: 'user',
            connector: globalEventFilters.length === 0 ? 'WHERE' : 'AND'
          };
        } else {
          // Event property (global for all event-based metrics)
          filterWithId = {
            ...newFilter,
            id: newFilter.id || `global-filter-${Date.now()}-${Math.random()}`,
            category: newFilter.category || 'global',
            connector: globalEventFilters.length === 0 ? 'WHERE' : 'AND'
          };
        }
      }

      onGlobalFiltersChange && onGlobalFiltersChange([...globalFilters, filterWithId]);
    }
    setIsGlobalFilterOpen(false);
  };

  const handleGlobalFirstTimeChange = (enabled) => {
    if (enabled) {
      // Remove any existing first_time filter first
      const withoutFirstTime = globalFilters.filter(f => f.category !== 'first_time');
      // Add the first time filter with correct structure
      const firstTimeFilter = {
        id: 'global-first-time-filter',
        field: 'firstTime',
        property: 'firstTime',
        operator: 'equals',
        value: true,
        category: 'first_time',
        connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND'
      };
      onGlobalFiltersChange && onGlobalFiltersChange([...withoutFirstTime, firstTimeFilter]);
    } else {
      // Remove the first time filter
      const withoutFirstTime = globalFilters.filter(f => f.category !== 'first_time');
      const updatedWithConnectors = withoutFirstTime.map((f, idx) => ({
        ...f,
        connector: idx === 0 ? 'WHERE' : 'AND'
      }));
      onGlobalFiltersChange && onGlobalFiltersChange(updatedWithConnectors);
    }
  };

  const handleRemoveGlobalFilter = (filterId) => {
    const updated = globalFilters.filter(f => f.id !== filterId);
    // Update connectors for event and transaction filters separately
    const eventFilters = updated.filter(f => 
      f.category !== 'transaction_properties' && 
      f.propertySource !== 'transaction' &&
      f.category !== 'first_time'
    );
    const transactionFilters = updated.filter(f => 
      f.category === 'transaction_properties' || 
      f.propertySource === 'transaction'
    );
    const updatedWithConnectors = updated.map((f) => {
      const isEventFilter = f.category !== 'transaction_properties' && 
                           f.propertySource !== 'transaction' &&
                           f.category !== 'first_time';
      const isTransactionFilter = f.category === 'transaction_properties' || 
                                 f.propertySource === 'transaction';
      const isFirstTime = f.category === 'first_time';
      
      if (isFirstTime) {
        return { ...f, connector: 'AND' };
      } else if (isEventFilter) {
        const eventIndex = eventFilters.findIndex(ef => ef.id === f.id);
        return { ...f, connector: eventIndex === 0 ? 'WHERE' : 'AND' };
      } else if (isTransactionFilter) {
        const txIndex = transactionFilters.findIndex(tf => tf.id === f.id);
        return { ...f, connector: txIndex === 0 ? 'WHERE' : 'AND' };
      }
      return f;
    });
    onGlobalFiltersChange && onGlobalFiltersChange(updatedWithConnectors);
  };

  const handleEditGlobalFilter = (filter) => {
    // First time filters are not editable - they can only be toggled
    if (filter.category === 'first_time') {
      return;
    }
    setSelectedGlobalFilterForEdit(filter);
    setIsGlobalFilterOpen(true);
  };

  // Get first event name for global filters (or use null/placeholder)
  const firstEventName = currentMetrics.length > 0 && 
    currentMetrics[0]?.events?.length > 0 
    ? currentMetrics[0].events[0].name 
    : null;

  return (
    <aside
      className="w-[320px] flex-shrink-0 border-l flex flex-col overflow-hidden relative"
      style={{
        fontFamily: "Raleway, sans-serif",
        backgroundColor: "#ffffff",
        borderColor: theme.border,
      }}
    >
      {/* Extend left border upward to touch top border above header */}
      <div
        className="absolute left-0"
        style={{
          top: "-56px",
          height: "56px",
          width: "1px",
          backgroundColor: theme.border,
          pointerEvents: "none",
          zIndex: 10,
        }}
      />
      <div className="p-4 overflow-y-auto flex-1 relative">
        {activeTab === "query" && (
          <>
            {/* Query config: Insight = metrics, Funnel = steps, Flow = anchor events, Retention = start/return events */}
            {isFlow ? (
              <FlowQueryConfig
                anchorEvents={flowAnchorEvents}
                onAnchorEventsChange={onFlowAnchorEventsChange || (() => {})}
                conversionWindow={flowConversionWindow}
                onConversionWindowChange={onFlowConversionWindowChange || (() => {})}
                conversionCriteria={flowConversionCriteria}
                onConversionCriteriaChange={onFlowConversionCriteriaChange || (() => {})}
                availableEvents={availableEvents}
                teamId={teamId}
              />
            ) : isRetention ? (
              <RetentionQueryConfig
                startEvent={retentionStartEvent}
                onStartEventChange={onRetentionStartEventChange || (() => {})}
                returnEvent={retentionReturnEvent}
                onReturnEventChange={onRetentionReturnEventChange || (() => {})}
                interval={retentionInterval}
                onIntervalChange={onRetentionIntervalChange || (() => {})}
                retentionRule={retentionRule}
                onRetentionRuleChange={onRetentionRuleChange || (() => {})}
                metric={retentionMetric}
                onMetricChange={onRetentionMetricChange || (() => {})}
                chartType={chartType}
                retentionIntervals={retentionIntervals}
                retentionGroupIndex={retentionLineIntervalIndex}
                onRetentionGroupIndexChange={setRetentionLineIntervalIndex}
                availableEvents={availableEvents}
                teamId={teamId}
              />
            ) : isFunnel ? (
              <FunnelQueryConfig
                funnelSteps={funnelSteps}
                onFunnelStepsChange={onFunnelStepsChange || (() => {})}
                availableEvents={availableEvents}
                teamId={teamId}
                funnelMeasuring={funnelMeasuring}
                onFunnelMeasuringChange={onFunnelMeasuringChange}
                funnelMeasuringProperty={funnelMeasuringProperty}
                onFunnelMeasuringPropertyChange={onFunnelMeasuringPropertyChange}
                funnelMeasuringAggregation={funnelMeasuringAggregation}
                onFunnelMeasuringAggregationChange={onFunnelMeasuringAggregationChange}
                funnelMeasuringPercentileValue={funnelMeasuringPercentileValue}
                onFunnelMeasuringPercentileValueChange={onFunnelMeasuringPercentileValueChange}
                funnelConversionWindow={funnelConversionWindow}
                onFunnelConversionWindowChange={onFunnelConversionWindowChange}
              />
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
                    Metrics
                  </span>
                  <button
                    type="button"
                    onClick={handleAddMetric}
                    className="text-sm transition-colors"
                    style={{ color: theme.textMuted }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = theme.textPrimary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
                  >
                    +
                  </button>
                </div>
            
                {/* Render Metrics */}
                {currentMetrics.length === 0 ? (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleAddMetric}
                  className="w-full flex items-center gap-2 px-3 py-2.5 border border-dashed rounded-lg text-left text-sm transition-colors"
                  style={{
                    borderColor: theme.border,
                    color: theme.textMuted,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#4B57AA";
                    e.currentTarget.style.color = theme.textPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.color = theme.textMuted;
                  }}
                >
                  <span className="text-gray-500">A</span>
                  <span className="flex-1">Add Metric</span>
                </button>
              </div>
            ) : (
              <div className="mb-6">
                {currentMetrics.map((metric, index) => (
                  <MetricBlock
                    key={metric.id || `metric-${index}`}
                    metric={metric}
                    metricIndex={index}
                    availableEvents={availableEvents}
                    teamId={teamId}
                    onUpdate={handleUpdateMetric}
                    onRemove={handleRemoveMetric}
                    onAddEvent={handleAddEvent}
                    onRemoveEvent={handleRemoveEvent}
                    onUpdateEventFilters={handleUpdateEventFilters}
                  />
                ))}
                <button
                  type="button"
                  onClick={handleAddMetric}
                  className="w-full flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-left text-sm transition-colors mt-2"
                  style={{
                    borderColor: theme.border,
                    color: theme.textMuted,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#4B57AA";
                    e.currentTarget.style.color = theme.textPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.color = theme.textMuted;
                  }}
                >
                  <span className="text-gray-500">
                    {String.fromCharCode(65 + currentMetrics.length)}
                  </span>
                  <span className="flex-1">Add Metric</span>
                </button>
              </div>
            )}
              </>
            )}

            {/* Global Filters Section */}
            <div className="flex items-center justify-between mb-2 mt-6">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
                Filter
              </span>
              <button
                type="button"
                className="text-sm transition-colors"
                style={{ color: theme.textMuted }}
                onMouseEnter={(e) => { e.currentTarget.style.color = theme.textPrimary; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
                onClick={handleGlobalFilterClick}
                ref={globalFilterButtonRef}
              >
                +
              </button>
            </div>

            <div className="mb-4">
              {globalEventFilters.length === 0 && globalTransactionFilters.length === 0 && globalCohortFilters.length === 0 ? (
                <div
                  className="px-3 py-2.5 border border-dashed rounded-lg text-sm cursor-pointer transition-colors mb-2"
                  style={{ borderColor: theme.border, color: theme.textMuted }}
                  onClick={handleGlobalFilterClick}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#4B57AA"; e.currentTarget.style.color = theme.textPrimary; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.textMuted; }}
                >
                  + Add filter
                </div>
              ) : (
                <div className="mb-2 space-y-2">
                  {[...globalCohortFilters, ...globalEventFilters, ...globalTransactionFilters].map((filter, index) => {
                    const isCohort = filter.category === 'cohort' || filter.propertySource === 'cohort';
                    const isTransaction = filter.category === 'transaction_properties' || filter.propertySource === 'transaction';
                    const isFirstRegularFilter = index === 0;
                    const ChipComponent = isCohort ? null : (isTransaction ? TransactionPropertyFilterChip : EventPropertyFilterChip);
                    return (
                      <div
                        key={filter.id || index}
                        ref={(el) => {
                          if (el) globalFilterChipRefs.current[filter.id || index] = el;
                        }}
                        className="flex items-center gap-2 flex-wrap"
                      >
                        {isCohort ? (
                          <div
                            className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50"
                            style={{ borderColor: theme.border, backgroundColor: '#f8fafc' }}
                            onClick={() => handleEditGlobalFilter(filter)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleEditGlobalFilter(filter)}
                          >
                            <span className="text-xs font-medium text-gray-500">{index === 0 ? 'WHERE' : 'AND'}</span>
                            <span className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-indigo-100 text-indigo-700" title="Cohort">C</span>
                            <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>{filter.propertyLabel || 'Cohort'}</span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRemoveGlobalFilter(filter.id || index); }}
                              className="ml-1 text-gray-400 hover:text-gray-600 p-0.5"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <ChipComponent
                            filter={filter}
                            isFirst={isFirstRegularFilter}
                            onEdit={() => handleEditGlobalFilter(filter)}
                            onRemove={() => handleRemoveGlobalFilter(filter.id || index)}
                            onUpdate={(updates) => {
                              const updated = globalFilters.map(f =>
                                f.id === filter.id ? { ...f, ...updates } : f
                              );
                              onGlobalFiltersChange && onGlobalFiltersChange(updated);
                            }}
                            teamId={teamId}
                            eventName={null}
                          />
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={handleGlobalFilterClick}
                    className="text-xs transition-colors mt-1"
                    style={{ color: theme.textMuted }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = theme.textPrimary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
                  >
                    + Add filter
                  </button>
                </div>
              )}
            </div>

            {/* First Time Filter (applies to both) */}
            {globalFirstTimeFilter && (
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg">
                    <span className="text-xs font-medium text-gray-700">WHERE</span>
                    <span className="text-sm text-gray-900 font-medium">First time only</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveGlobalFilter(globalFirstTimeFilter.id)}
                      className="text-gray-500 hover:text-gray-700"
                      title="Remove First time only"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
                Breakdown
              </span>
            </div>
            
            {/* Existing breakdowns list */}
            {currentBreakdowns.length > 0 && (
              <div className="space-y-2 mb-3">
                {currentBreakdowns.map((bd, index) => (
                  <div
                    key={index}
                    className="px-3 py-2.5 border rounded-lg text-sm flex items-center justify-between gap-2"
                    style={{ borderColor: theme.border }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', index.toString());
                      e.currentTarget.style.opacity = '0.5';
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                      if (dragIndex === index) return;
                      
                      const newBreakdowns = [...currentBreakdowns];
                      const [removed] = newBreakdowns.splice(dragIndex, 1);
                      newBreakdowns.splice(index, 0, removed);
                      handleBreakdownsChange(newBreakdowns);
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {/* Drag handle */}
                      <div className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <GripVertical className="h-4 w-4" />
                      </div>
                      
                      {/* Property name */}
                      <span 
                        className="font-medium truncate" 
                        style={{ color: theme.textPrimary }}
                        title={bd.propertyLabel || bd.property || 'Unnamed property'}
                      >
                        {bd.propertyLabel || bd.property || 'Unnamed property'}
                      </span>
                      
                      {/* Source badge */}
                      <span 
                        className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 flex-shrink-0"
                        title={
                          bd.propertySource === 'user' 
                            ? 'User Property' 
                            : bd.propertySource === 'transaction' 
                            ? 'Transaction Property' 
                            : 'Event Property'
                        }
                      >
                        {bd.propertySource === 'user' ? 'U' : 
                         bd.propertySource === 'transaction' ? 'T' : 'E'}
                      </span>
                      
                      {/* Limit - informational only, actual limit controlled by frontend */}
                      <span className="text-xs flex-shrink-0" style={{ color: theme.textMuted }}>
                        (Up to 50)
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Edit button */}
                      <button
                        type="button"
                        onClick={() => {
                          setBreakdownPickerIndex(index);
                          setBreakdownPickerOpen(true);
                        }}
                        className="text-xs px-2 py-1 rounded transition-colors"
                        style={{ 
                          backgroundColor: theme.bgRaised, 
                          color: theme.textPrimary 
                        }}
                        onMouseEnter={(e) => { 
                          e.currentTarget.style.backgroundColor = "#e5e7eb"; 
                        }}
                        onMouseLeave={(e) => { 
                          e.currentTarget.style.backgroundColor = theme.bgRaised; 
                        }}
                      >
                        Edit
                      </button>
                      
                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newBreakdowns = currentBreakdowns.filter((_, i) => i !== index);
                          handleBreakdownsChange(newBreakdowns);
                        }}
                        className="text-xs text-red-600 hover:text-red-700 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add breakdown button */}
            <button
              ref={breakdownPickerRef}
              type="button"
              onClick={() => {
                setBreakdownPickerIndex(null);
                setBreakdownPickerOpen(true);
              }}
              className="px-3 py-2.5 border border-dashed rounded-lg text-sm cursor-pointer transition-colors w-full"
              style={{ borderColor: theme.border, color: theme.textMuted }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.borderColor = "#4B57AA"; 
                e.currentTarget.style.color = theme.textPrimary; 
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.borderColor = theme.border; 
                e.currentTarget.style.color = theme.textMuted; 
              }}
            >
              + Add breakdown
            </button>
            
            {/* GlobalPropertyFilterModule for breakdown selection */}
            <GlobalPropertyFilterModule
              isOpen={breakdownPickerOpen}
              onClose={() => {
                setBreakdownPickerOpen(false);
                setBreakdownPickerIndex(null);
              }}
              triggerRef={breakdownPickerRef}
              teamId={teamId}
              isBreakdownPicker={true}
              existingFilter={breakdownPickerIndex !== null ? currentBreakdowns[breakdownPickerIndex] : null}
              onGlobalFilterAdd={(filter) => {
                // Convert filter to breakdown format
                // Cohort as breakdown = break by all cohorts (no value); like Country - user does not pick each cohort
                const isRetentionBreakdown = isRetention === true;
                const newBreakdown = {
                  property: filter.property || filter.field,
                  propertyLabel: filter.propertyLabel || filter.property || filter.field || 'Unnamed property',
                  propertySource: filter.propertySource || filter.category || 'event',
                  limit: filter.limit || currentBreakdowns[breakdownPickerIndex]?.limit || 10,
                  // Do NOT pass value for cohort breakdown - backend uses all cohorts
                  ...((filter.propertySource === 'cohort' || filter.category === 'cohort') && { value: undefined }),
                  ...(isRetentionBreakdown ? {
                    step: currentBreakdowns[breakdownPickerIndex]?.step ?? 1,
                    limit: currentBreakdowns[breakdownPickerIndex]?.limit ?? 12,
                    showMax: currentBreakdowns[breakdownPickerIndex]?.showMax === true,
                    sortOrder: currentBreakdowns[breakdownPickerIndex]?.sortOrder ?? 'value-desc',
                    includeOther: currentBreakdowns[breakdownPickerIndex]?.includeOther !== false,
                  } : {}),
                };
                
                if (breakdownPickerIndex !== null) {
                  // Editing existing breakdown
                  const newBreakdowns = [...currentBreakdowns];
                  newBreakdowns[breakdownPickerIndex] = newBreakdown;
                  handleBreakdownsChange(newBreakdowns);
                } else {
                  // Adding new breakdown
                  handleBreakdownsChange([...currentBreakdowns, newBreakdown]);
                }
                setBreakdownPickerOpen(false);
                setBreakdownPickerIndex(null);
              }}
              firstTimeOnly={false} // Exclude first time for breakdowns
              onFirstTimeChange={null} // Not applicable
              allowTransactionFilters={!isRetention}
            />

            {/* Retention-only breakdown controls (Mixpanel parity) */}
            {isRetention && currentBreakdowns.length > 0 && (
              <div className="mt-3 grid grid-cols-1 gap-2">
                {(currentBreakdowns[0]?.propertySource || 'event') !== 'user' && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs" style={{ color: theme.textMuted }}>Breakdown on</span>
                    <div className="w-[180px]">
                      <InlineDropdown
                        value={currentBreakdowns[0]?.step === 2 ? 2 : 1}
                        onChange={(v) => {
                          const step = Number(v) === 2 ? 2 : 1;
                          const updated = { ...currentBreakdowns[0], step };
                          handleBreakdownsChange([updated]);
                        }}
                        options={[
                          { value: 1, label: 'Step 1' },
                          { value: 2, label: 'Step 2' },
                        ]}
                        widthClass="min-w-[180px]"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Top values</span>
                  <div className="w-[180px]">
                    <InlineDropdown
                      value={currentBreakdowns[0]?.showMax === true ? 50 : (typeof currentBreakdowns[0]?.limit === 'number' ? Math.min(Math.max(1, currentBreakdowns[0].limit), 50) : 12)}
                      onChange={(v) => {
                        const n = Number(v);
                        const showMax = n === 50;
                        const limit = showMax ? 12 : n;
                        const updated = { ...currentBreakdowns[0], limit, showMax };
                        handleBreakdownsChange([updated]);
                      }}
                      options={[
                        { value: 12, label: 'Top 12' },
                        { value: 25, label: 'Top 25' },
                        { value: 50, label: 'Show max (50)' },
                      ]}
                      widthClass="min-w-[180px]"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Sort</span>
                  <div className="w-[180px]">
                    <InlineDropdown
                      value={currentBreakdowns[0]?.sortOrder || 'value-desc'}
                      onChange={(v) => {
                        const updated = { ...currentBreakdowns[0], sortOrder: v };
                        handleBreakdownsChange([updated]);
                      }}
                      options={[
                        { value: 'value-desc', label: 'Value (desc)' },
                        { value: 'value-asc', label: 'Value (asc)' },
                        { value: 'segment-a-z', label: 'Segment (A→Z)' },
                        { value: 'segment-z-a', label: 'Segment (Z→A)' },
                      ]}
                      widthClass="min-w-[180px]"
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "chart" && (
          <>
            {/* AXIS */}
            <div className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "#6b7280" }}>
              Axis
            </div>

            {/* Left Y-Axis */}
            <div className="mb-6 pb-6 border-b" style={{ borderColor: theme.border }}>
              <div className="text-sm font-medium mb-3" style={{ color: "#000000" }}>Left Y-Axis</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Axis</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, visible: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.visible ? "#4B57AA" : theme.bgRaised, color: leftY.visible ? "#fff" : theme.textMuted }}>Visible</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, visible: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !leftY.visible ? "#4B57AA" : theme.bgRaised, color: !leftY.visible ? "#fff" : theme.textMuted }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Label</span>
                  <input type="text" value={leftY.label} onChange={(e) => setLeftY((s) => ({ ...s, label: e.target.value }))} placeholder="" className="w-[180px] px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Unit</span>
                  <div className="w-[180px] flex flex-col gap-1">
                    <div className="flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                      {["$", "%", "#", "Custom"].map((u, index) => (
                        <button key={u} type="button" onClick={() => setLeftY((s) => ({ ...s, unit: u }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.unit === u ? "#4B57AA" : theme.bgRaised, color: leftY.unit === u ? "#fff" : theme.textMuted, borderRight: index < 3 ? `1px solid ${theme.border}` : "none" }}>{u}</button>
                      ))}
                    </div>
                    {leftY.unit === "Custom" && (
                      <input type="text" value={leftY.customUnit ?? ""} onChange={(e) => setLeftY((s) => ({ ...s, customUnit: e.target.value }))} placeholder="e.g. users" className="w-full px-2 h-7 border rounded-lg text-xs outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Abbreviation</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, abbreviation: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.abbreviation ? "#4B57AA" : theme.bgRaised, color: leftY.abbreviation ? "#fff" : theme.textMuted }}>Yes</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, abbreviation: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !leftY.abbreviation ? "#4B57AA" : theme.bgRaised, color: !leftY.abbreviation ? "#fff" : theme.textMuted }}>No</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Decimals</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, decimals: ".0" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.decimals === ".0" ? "#4B57AA" : theme.bgRaised, color: leftY.decimals === ".0" ? "#fff" : theme.textMuted }}>+.0</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, decimals: ".00" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.decimals === ".00" ? "#4B57AA" : theme.bgRaised, color: leftY.decimals === ".00" ? "#fff" : theme.textMuted }}>.00 →</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Preview</span>
                  <span className="text-xs font-medium w-[180px]" style={{ color: theme.textPrimary }}>
                    {(() => {
                      const v = 1250000;
                      const dp = leftY.decimals === ".00" ? 2 : 0;
                      const u = leftY.unit ?? "#";
                      const cu = leftY.customUnit ?? "";
                      const abbr = leftY.abbreviation !== false;
                      const pre = u === "$" ? "$" : "";
                      const suf = u === "%" ? "%" : u === "Custom" ? (cu || "unit") : "";
                      if (abbr) return `${pre}1.25M${suf}`;
                      return `${pre}${v.toLocaleString(undefined, { minimumFractionDigits: dp })}${suf}`;
                    })()}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Threshold Bounds</span>
                  <div className="w-[180px] grid grid-cols-2 gap-2">
                    <div>
                      <input type="text" value={leftY.thresholdMin} onChange={(e) => setLeftY((s) => ({ ...s, thresholdMin: e.target.value }))} placeholder="Min" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                    </div>
                    <div>
                      <input type="text" value={leftY.thresholdMax} onChange={(e) => setLeftY((s) => ({ ...s, thresholdMax: e.target.value }))} placeholder="Max" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs flex items-center gap-1" style={{ color: theme.textMuted }}>Out of Bounds <Info className="w-3 h-3" /></span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, outOfBounds: "visible" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.outOfBounds === "visible" ? "#4B57AA" : theme.bgRaised, color: leftY.outOfBounds === "visible" ? "#fff" : theme.textMuted }}>Visible</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, outOfBounds: "hidden" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.outOfBounds === "hidden" ? "#4B57AA" : theme.bgRaised, color: leftY.outOfBounds === "hidden" ? "#fff" : theme.textMuted }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Scale</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, scale: "linear" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.scale === "linear" ? "#4B57AA" : theme.bgRaised, color: leftY.scale === "linear" ? "#fff" : theme.textMuted }}>Linear</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, scale: "log" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.scale === "log" ? "#4B57AA" : theme.bgRaised, color: leftY.scale === "log" ? "#fff" : theme.textMuted }}>Logarithmic</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Y-Axis */}
            <div className="mb-6 pb-6 border-b" style={{ borderColor: theme.border }}>
              <div className="text-sm font-medium mb-3" style={{ color: "#000000" }}>Right Y-Axis</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Axis</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, visible: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.visible ? "#4B57AA" : theme.bgRaised, color: rightY.visible ? "#fff" : theme.textMuted }}>Visible</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, visible: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !rightY.visible ? "#4B57AA" : theme.bgRaised, color: !rightY.visible ? "#fff" : theme.textMuted }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Label</span>
                  <input type="text" value={rightY.label} onChange={(e) => setRightY((s) => ({ ...s, label: e.target.value }))} className="w-[180px] px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Unit</span>
                  <div className="w-[180px] flex flex-col gap-1">
                    <div className="flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                      {["$", "%", "#", "Custom"].map((u, index) => (
                        <button key={u} type="button" onClick={() => setRightY((s) => ({ ...s, unit: u }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.unit === u ? "#4B57AA" : theme.bgRaised, color: rightY.unit === u ? "#fff" : theme.textMuted, borderRight: index < 3 ? `1px solid ${theme.border}` : "none" }}>{u}</button>
                      ))}
                    </div>
                    {rightY.unit === "Custom" && (
                      <input type="text" value={rightY.customUnit ?? ""} onChange={(e) => setRightY((s) => ({ ...s, customUnit: e.target.value }))} placeholder="e.g. users" className="w-full px-2 h-7 border rounded-lg text-xs outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Abbreviation</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, abbreviation: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.abbreviation ? "#4B57AA" : theme.bgRaised, color: rightY.abbreviation ? "#fff" : theme.textMuted }}>Yes</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, abbreviation: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !rightY.abbreviation ? "#4B57AA" : theme.bgRaised, color: !rightY.abbreviation ? "#fff" : theme.textMuted }}>No</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Decimals</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, decimals: ".0" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.decimals === ".0" ? "#4B57AA" : theme.bgRaised, color: rightY.decimals === ".0" ? "#fff" : theme.textMuted }}>+.0</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, decimals: ".00" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.decimals === ".00" ? "#4B57AA" : theme.bgRaised, color: rightY.decimals === ".00" ? "#fff" : theme.textMuted }}>.00 →</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Preview</span>
                  <span className="text-xs font-medium w-[180px]" style={{ color: theme.textPrimary }}>
                    {(() => {
                      const v = 1250000;
                      const dp = rightY.decimals === ".00" ? 2 : 0;
                      const u = rightY.unit ?? "#";
                      const cu = rightY.customUnit ?? "";
                      const abbr = rightY.abbreviation !== false;
                      const pre = u === "$" ? "$" : "";
                      const suf = u === "%" ? "%" : u === "Custom" ? (cu || "unit") : "";
                      if (abbr) return `${pre}1.25M${suf}`;
                      return `${pre}${v.toLocaleString(undefined, { minimumFractionDigits: dp })}${suf}`;
                    })()}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Threshold Bounds</span>
                  <div className="w-[180px] grid grid-cols-2 gap-2">
                    <div>
                      <input type="text" value={rightY.thresholdMin} onChange={(e) => setRightY((s) => ({ ...s, thresholdMin: e.target.value }))} placeholder="Min" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                    </div>
                    <div>
                      <input type="text" value={rightY.thresholdMax} onChange={(e) => setRightY((s) => ({ ...s, thresholdMax: e.target.value }))} placeholder="Max" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs flex items-center gap-1" style={{ color: theme.textMuted }}>Out of Bounds <Info className="w-3 h-3" /></span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, outOfBounds: "visible" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.outOfBounds === "visible" ? "#4B57AA" : theme.bgRaised, color: rightY.outOfBounds === "visible" ? "#fff" : theme.textMuted }}>Visible</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, outOfBounds: "hidden" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.outOfBounds === "hidden" ? "#4B57AA" : theme.bgRaised, color: rightY.outOfBounds === "hidden" ? "#fff" : theme.textMuted }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Scale</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, scale: "linear" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.scale === "linear" ? "#4B57AA" : theme.bgRaised, color: rightY.scale === "linear" ? "#fff" : theme.textMuted }}>Linear</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, scale: "log" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.scale === "log" ? "#4B57AA" : theme.bgRaised, color: rightY.scale === "log" ? "#fff" : theme.textMuted }}>Logarithmic</button>
                  </div>
                </div>
              </div>
            </div>

            {/* X-Axis */}
            <div className="mb-6 pb-6 border-b" style={{ borderColor: theme.border }}>
              <div className="text-sm font-medium mb-3" style={{ color: "#000000" }}>X-Axis</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Axis</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }}>
                    <button type="button" onClick={() => setXAxis((s) => ({ ...s, visible: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: xAxis.visible ? "#4B57AA" : theme.bgRaised, color: xAxis.visible ? "#fff" : theme.textMuted }}>Visible</button>
                    <button type="button" onClick={() => setXAxis((s) => ({ ...s, visible: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !xAxis.visible ? "#4B57AA" : theme.bgRaised, color: !xAxis.visible ? "#fff" : theme.textMuted }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: theme.textMuted }}>Label</span>
                  <input type="text" value={xAxis.label} onChange={(e) => setXAxis((s) => ({ ...s, label: e.target.value }))} className="w-[180px] px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }} />
                </div>
              </div>
            </div>

            {/* Metric Axis Assignment - L/R per metric; for funnels show funnel steps as one series (all funnel chart types) */}
            <div className="mb-6 pb-6 border-b mt-6" style={{ borderColor: theme.border }}>
              <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "#6b7280" }}>Axis Assignment</div>
              {isFunnel && funnelSteps.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm truncate flex-1 min-w-0" style={{ color: theme.textPrimary }} title={funnelSteps.map((s) => s.label || s.eventName || "Step").join(" through ") || "Funnel steps"}>
                      {(() => {
                        const funnelLabel = funnelSteps.map((s) => s.label || s.eventName || "Step").join(" through ") || "Funnel steps";
                        return funnelLabel.length > 24 ? funnelLabel.slice(0, 24) + "…" : funnelLabel;
                      })()}
                    </span>
                    <div className="flex border overflow-hidden rounded-lg shrink-0" style={{ borderColor: theme.border }}>
                      <button
                        type="button"
                        onClick={() => setFunnelAxis("left")}
                        className="px-3 h-7 text-xs font-medium transition-colors"
                        style={{ backgroundColor: funnelAxis === "left" ? "#4B57AA" : theme.bgRaised, color: funnelAxis === "left" ? "#fff" : theme.textMuted }}
                      >
                        L
                      </button>
                      <button
                        type="button"
                        onClick={() => setFunnelAxis("right")}
                        className="px-3 h-7 text-xs font-medium transition-colors"
                        style={{ backgroundColor: funnelAxis === "right" ? "#4B57AA" : theme.bgRaised, color: funnelAxis === "right" ? "#fff" : theme.textMuted }}
                      >
                        R
                      </button>
                    </div>
                  </div>
                </div>
              ) : ((currentMetrics || []).filter((m) => m?.events?.length > 0).length > 0) ? (
                <div className="space-y-2">
                  {(currentMetrics || [])
                    .map((metric, origIndex) => ({ metric, origIndex }))
                    .filter(({ metric: m }) => m?.events?.length > 0)
                    .map(({ metric, origIndex }) => {
                      const label = metric.events?.map((e) => e.name).filter(Boolean).join(" + ") || `Metric ${String.fromCharCode(65 + origIndex)}`;
                      const axis = metric.axis || "left";
                      return (
                        <div key={metric.id || origIndex} className="flex items-center justify-between gap-2">
                          <span className="text-sm truncate flex-1 min-w-0" style={{ color: theme.textPrimary }} title={label}>
                            {label.length > 24 ? label.slice(0, 24) + "…" : label}
                          </span>
                          <div className="flex border overflow-hidden rounded-lg shrink-0" style={{ borderColor: theme.border }}>
                            <button
                              type="button"
                              onClick={() => handleUpdateMetric(origIndex, { ...metric, axis: "left" })}
                              className="px-3 h-7 text-xs font-medium transition-colors"
                              style={{ backgroundColor: axis === "left" ? "#4B57AA" : theme.bgRaised, color: axis === "left" ? "#fff" : theme.textMuted }}
                            >
                              L
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateMetric(origIndex, { ...metric, axis: "right" })}
                              className="px-3 h-7 text-xs font-medium transition-colors"
                              style={{ backgroundColor: axis === "right" ? "#4B57AA" : theme.bgRaised, color: axis === "right" ? "#fff" : theme.textMuted }}
                            >
                              R
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="px-4 py-6 border border-dashed rounded-lg text-center text-sm" style={{ borderColor: theme.border, color: "#6b7280", lineHeight: 1.5 }}>
                  <span className="block">Add metrics in the Query tab, then assign each to</span>
                  <span className="block font-medium mt-0.5">Left (L) or Right (R) axis</span>
                </div>
              )}
            </div>

            {/* THEMES */}
            <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "#6b7280" }}>Themes</div>
            <div className="mb-2">
              <div className="flex items-center gap-2 px-2 h-7 border rounded-lg" style={{ backgroundColor: theme.bgRaised, borderColor: theme.border }}>
                <Search className="w-4 h-4 flex-shrink-0" style={{ color: theme.textMuted }} />
                <input type="text" value={themeSearch} onChange={(e) => setThemeSearch(e.target.value)} placeholder="Find a theme" className="flex-1 min-w-0 bg-transparent text-sm outline-none" style={{ color: theme.textPrimary }} />
              </div>
            </div>
            <div className="space-y-1.5">
              {CHART_THEMES.filter((t) => !themeSearch || t.label.toLowerCase().includes(themeSearch.toLowerCase())).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedThemeId(t.id)}
                  className="w-full flex items-center gap-3 px-2 py-2 border rounded-lg text-left transition-colors"
                  style={{
                    borderColor: selectedThemeId === t.id ? "#4B57AA" : theme.border,
                    backgroundColor: selectedThemeId === t.id ? "rgba(45, 45, 45, 0.06)" : "transparent",
                    color: theme.textPrimary,
                  }}
                >
                  <span className="flex gap-0.5 flex-shrink-0">
                    {t.colors.map((c, i) => (
                      <span key={i} className="w-4 h-4" style={{ backgroundColor: c }} />
                    ))}
                  </span>
                  <span className="text-sm truncate">{t.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {activeTab === "annotations" && (
          <div className="text-sm" style={{ color: theme.textMuted }}>
            Add notes on the timeline. Visual markers will appear on the chart.
          </div>
        )}
      </div>

      {/* Global Unified Filter Module */}
      {isGlobalFilterOpen && (
        <GlobalPropertyFilterModule
          isOpen={isGlobalFilterOpen}
          onClose={() => {
            setIsGlobalFilterOpen(false);
            setSelectedGlobalFilterForEdit(null);
          }}
          // Always anchor the popup to the same "+ Filter" button used when
          // opening the selector the first time, so editing a filter opens
          // the selector in exactly the same position as initial create.
          triggerRef={globalFilterButtonRef}
          teamId={teamId}
          existingFilter={selectedGlobalFilterForEdit}
          onGlobalFilterAdd={handleGlobalFiltersChange}
          firstTimeOnly={!!globalFirstTimeFilter}
          onFirstTimeChange={handleGlobalFirstTimeChange}
          allowTransactionFilters={!isRetention}
        />
      )}

      <div className="p-2 border-t flex justify-end" style={{ borderColor: theme.border }}>
        <button
          type="button"
          className="p-1.5 transition-colors"
          style={{ color: theme.textMuted }}
          onMouseEnter={(e) => { e.currentTarget.style.color = theme.textPrimary; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
          title="Collapse panel"
        >
          →
        </button>
      </div>

    </aside>
  );
}
